import { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import { MarkdownEditorLazy as MarkdownEditor } from "src/components/ui/markdown-editor-lazy";
import { REDACTED, SecretInput } from "src/components/ui/secret-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { ErrorAlert, SuccessAlert, WarningAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { DatadogIntegrationFormUpdateIntegrationMutation } from "./__generated__/DatadogIntegrationFormUpdateIntegrationMutation.graphql";
import { DatadogIntegrationFormValidateIntegrationMutation } from "./__generated__/DatadogIntegrationFormValidateIntegrationMutation.graphql";
import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";

// Define the mutations
const updateIntegrationMutation = graphql`
  mutation DatadogIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation DatadogIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
      warnings
    }
  }
`;

interface DatadogIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  editMode?: {
    integrationId: string;
    datadogSite: string;
    redactedApiKey: string;
    redactedAppKey: string;
  };
}

export default function DatadogIntegrationForm({
  onComplete,
  onCancel,
  editMode,
}: DatadogIntegrationFormProps) {
  const [apiKey, setApiKey] = useState(editMode?.redactedApiKey ? REDACTED : "");
  const [appKey, setAppKey] = useState(editMode?.redactedAppKey ? REDACTED : "");
  const [apiKeyTouched, setApiKeyTouched] = useState(!editMode);
  const [appKeyTouched, setAppKeyTouched] = useState(!editMode);
  const [datadogSite, setDatadogSite] = useState(editMode?.datadogSite ?? "datadoghq.com");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<ReadonlyArray<string> | null>(null);
  const [isTestSuccessful, setIsTestSuccessful] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const datadogSites = [
    { value: "datadoghq.com", label: "US1 (datadoghq.com)" },
    { value: "us3.datadoghq.com", label: "US3 (us3.datadoghq.com)" },
    { value: "us5.datadoghq.com", label: "US5 (us5.datadoghq.com)" },
    { value: "datadoghq.eu", label: "EU1 (datadoghq.eu)" },
    { value: "ap1.datadoghq.com", label: "AP1 (ap1.datadoghq.com)" },
    { value: "ap2.datadoghq.com", label: "AP2 (ap2.datadoghq.com)" }
  ];

  // Set up the mutations
  const [commitMutation, isMutationInFlight] = useMutation<DatadogIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<DatadogIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  const buildCredentials = () => ({
    datadog_site: datadogSite,
    api_key: apiKeyTouched ? apiKey.trim() : "[redacted]",
    app_key: appKeyTouched ? appKey.trim() : "[redacted]",
  });

  const validateFields = (): boolean => {
    if (editMode) {
      const apiKeyChanged = apiKeyTouched && apiKey.trim();
      const appKeyChanged = appKeyTouched && appKey.trim();
      if (!apiKeyChanged && !appKeyChanged) {
        setFormError("Enter at least one new key to update");
        return false;
      }
      if (apiKeyTouched && !apiKey.trim()) {
        setFormError("API Key cannot be empty. Leave the field untouched to keep the current key.");
        return false;
      }
      if (appKeyTouched && !appKey.trim()) {
        setFormError("Application Key cannot be empty. Leave the field untouched to keep the current key.");
        return false;
      }
    } else {
      if (!apiKey.trim()) {
        setFormError("API Key is required");
        return false;
      }
      if (!appKey.trim()) {
        setFormError("Application Key is required");
        return false;
      }
    }
    return true;
  };

  // Handle testing the integration
  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setValidationWarnings(null);
    setIsTestSuccessful(false);

    if (!validateFields()) return;

    const input = {
      integrationType: "DATADOG" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(editMode && { integrationId: editMode.integrationId }),
    };

    // Execute the validation mutation
    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          setIsTestSuccessful(false);
          return;
        }

        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          setIsTestSuccessful(false);
          return;
        }

        // Success!
        setValidationSuccess("Credentials validated successfully!");
        setIsTestSuccessful(true);

        // Display warnings if any (e.g., spans validation failed)
        if (response.validateIntegration.warnings && response.validateIntegration.warnings.length > 0) {
          setValidationWarnings(response.validateIntegration.warnings);
        }
      },
      onError: error => {
        setFormError(error.message);
        setIsTestSuccessful(false);
      }
    });
  };

  // Handle saving the integration
  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    if (!validateFields()) return;

    const input = {
      integrationType: "DATADOG" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(editMode && { integrationId: editMode.integrationId }),
      ...(!editMode && description.trim() && { description: description.trim() }),
    };

    // Execute the mutation
    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          const errorMessage = errors.map(e => e.message).join(", ");
          setFormError(errorMessage);
          onComplete(false);
          return;
        }

        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }

        // Success!
        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  const resetValidation = () => {
    setValidationSuccess(null);
    setValidationWarnings(null);
    setIsTestSuccessful(false);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">API Key Configuration</h3>

          <div className="space-y-4">
            {editMode ? (
              <div className="space-y-2">
                <Label className="text-muted-foreground">Datadog Site</Label>
                <div className="text-foreground text-sm py-2">
                  {datadogSites.find(s => s.value === datadogSite)?.label ?? datadogSite}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="datadog-site" className="text-muted-foreground">Datadog Site</Label>
                <Tooltip
                  content={!canModify ? "You don't have permission to modify integrations" : null}
                  position="top"
                  asChild
                >
                  <span className="block w-full">
                    <Select
                      value={datadogSite}
                      onValueChange={(value) => {
                        setDatadogSite(value);
                        resetValidation();
                      }}
                      disabled={!canModify}
                    >
                      <SelectTrigger id="datadog-site" className="bg-popover text-foreground border-border">
                        <SelectValue placeholder="Select Datadog site" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover text-foreground border-border">
                        {datadogSites.map((site) => (
                          <SelectItem key={site.value} value={site.value} className="text-foreground">
                            {site.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </span>
                </Tooltip>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="api-key" className="text-muted-foreground">API Key</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="api-key"
                    autoFocus={false}
                    value={apiKey}
                    fieldLabel="API Key"
                    onChange={(e) => {
                      if (editMode && !apiKeyTouched) {
                        setApiKeyTouched(true);
                      }
                      setApiKey(e.target.value);
                      resetValidation();
                    }}
                    placeholder="Enter your Datadog API Key"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-key" className="text-muted-foreground">Application Key</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="app-key"
                    autoFocus={false}
                    value={appKey}
                    fieldLabel="Application Key"
                    onChange={(e) => {
                      if (editMode && !appKeyTouched) {
                        setAppKeyTouched(true);
                      }
                      setAppKey(e.target.value);
                      resetValidation();
                    }}
                    placeholder="Enter your Datadog Application Key"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            {!editMode && (
              <div className="space-y-2">
                <Label htmlFor="description" className="text-muted-foreground">Agent Descriptor (Optional)</Label>
                <Tooltip
                  content={!canModify ? "You don't have permission to modify integrations" : null}
                  position="top"
                >
                  <div className="border border-border rounded-md overflow-hidden">
                    <MarkdownEditor
                      value={description}
                      onChange={(value) => setDescription(value)}
                      height="200px"
                      placeholder="Describe how the AI agent should use this Datadog integration if multiple datadog integrations are connected..."
                      autoFocus={false}
                      readOnly={!canModify}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>

      {formError && (
        <ErrorAlert icon="none">
          {formError}
        </ErrorAlert>
      )}

      {validationSuccess && (
        <SuccessAlert icon="none">
          {validationSuccess}
        </SuccessAlert>
      )}

      {validationWarnings && validationWarnings.length > 0 && (
        <WarningAlert icon="triangle">
          <div className="font-semibold mb-1">Warnings:</div>
          <ul className="list-disc list-inside space-y-1">
            {validationWarnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </WarningAlert>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              onClick={handleSave}
              disabled={!isTestSuccessful || isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isMutationInFlight ? "Saving..." : editMode ? "Update Keys" : "Connect"}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
